import Papa from "papaparse";
import { z, ZodError, ZodObject, ZodRawShape, ZodTypeAny } from "zod";

export type CsvError = {
  row: number;
  column: string;
  message: string;
  value?: string;
};

export const validateCsv = <T extends ZodRawShape>(
  file: File,
  schema: ZodObject<T>,
): Promise<CsvError[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const errors: CsvError[] = [];

        const fileHeaders = results.meta.fields || [];
        const schemaShape = schema.shape;
        const missingColumns: string[] = [];

        for (const key in schemaShape) {
          const fieldSchema = schemaShape[key] as ZodTypeAny;
          const isOptional = fieldSchema.safeParse(undefined).success;

          if (!isOptional && !fileHeaders.includes(key)) {
            missingColumns.push(key);
          }
        }

        const unknownColumns = fileHeaders.filter(
          (header) => !(header in schemaShape),
        );

        if (unknownColumns.length > 0) {
          errors.push({
            row: 1,
            column: "Headers",
            message: `Unknown columns: ${unknownColumns.join(", ")}`,
          });
          resolve(errors);
          return;
        }

        if (missingColumns.length > 0) {
          errors.push({
            row: 1,
            column: "Headers",
            message: `Missing required columns: ${missingColumns.join(", ")}`,
          });
          resolve(errors);
          return;
        }
        results.data.forEach((row, index) => {
          const rowNumber = index + 2;

          const result = schema.safeParse(row);

          if (!result.success) {
            const error = result.error as ZodError;
            error.issues.forEach((zodError: z.ZodIssue) => {
              const path = zodError.path.join(".") || "Global";
              const value =
                zodError.path.length > 0
                  ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (row as any)[zodError.path[0] as string | number]
                  : undefined;

              errors.push({
                row: rowNumber,
                column: path,
                message: zodError.message,
                value,
              });
            });
          }
        });

        if (results.errors.length > 0) {
          results.errors.forEach((parseError) => {
            errors.push({
              row: parseError.row ? parseError.row + 2 : 0,
              column: "Structure",
              message: parseError.message,
            });
          });
        }

        resolve(errors);
      },
      error: (error) => {
        reject(error);
      },
    });
  });
};
